import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { PromptCollection, Prompt } from "@/lib/types";

export function useCollectionDetail(collectionId: string) {
  return useQuery({
    queryKey: ["collection-detail", collectionId],
    queryFn: async (): Promise<PromptCollection | null> => {
      if (!collectionId) return null;

      const { data, error } = await supabase
        .from("prompt_collections")
        .select("*")
        .eq("id", collectionId)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        description: data.description,
        isPublic: data.is_public,
        emoji: data.emoji || "📁",
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    },
    enabled: !!collectionId,
  });
}

export function useCollectionPrompts(collectionId: string) {
  return useQuery({
    queryKey: ["collection-prompts", collectionId],
    queryFn: async (): Promise<Prompt[]> => {
      if (!collectionId) return [];

      const { data, error } = await supabase
        .from("collection_prompts")
        .select(`
          display_order,
          prompts!inner(
            id,
            user_id,
            title,
            content,
            description,
            category,
            created_at,
            copy_count,
            is_remix,
            remix_of,
            geo_metadata
          )
        `)
        .eq("collection_id", collectionId)
        .order("display_order", { ascending: true });

      if (error) throw error;

      // Fetch profile data for each prompt
      const promptsWithProfiles = await Promise.all(
        (data || []).map(async (item: any) => {
          const prompt = item.prompts;
          
          // Fetch the user profile
          const { data: profile } = await supabase
            .from("public_profiles")
            .select("username, avatar_id")
            .eq("id", prompt.user_id)
            .single();

          return {
            id: prompt.id,
            userId: prompt.user_id,
            username: profile?.username || "Anonymous",
            avatarId: profile?.avatar_id || 1,
            title: prompt.title || "",
            content: prompt.content || "",
            description: prompt.description,
            category: prompt.category || "general-chat",
            likes: 0,
            comments: 0,
            createdAt: prompt.created_at,
            copyCount: prompt.copy_count,
            isRemix: prompt.is_remix,
            remixOf: prompt.remix_of,
            geoMetadata: prompt.geo_metadata,
          };
        })
      );

      return promptsWithProfiles;
    },
    enabled: !!collectionId,
  });
}
